import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Button,
  Chip,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert as MuiAlert,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import leaveApiService from "../../../services/leave-api";
import {
  LeaveRequest,
  LeaveRequestStatus,
  formatDate,
} from "../../../types/leave";
import { useBadgeRefresh } from "../../../contexts/BadgeRefreshContext";

const PendingApprovals: React.FC = () => {
  const { t } = useTranslation();
  const { triggerRefresh } = useBadgeRefresh();
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [denyDialogOpen, setDenyDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<LeaveRequest | null>(
    null,
  );
  const [denialReason, setDenialReason] = useState("");
  const [isPlanned, setIsPlanned] = useState<boolean | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const fetchPendingRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await leaveApiService.getMyPendingRequests();
      setRequests(data);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("error.failedToFetchPendingRequests"),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingRequests();
  }, []);

  const openApproveDialog = (request: LeaveRequest) => {
    setSelectedRequest(request);
    setIsPlanned(null);
    setApproveDialogOpen(true);
  };

  const handleApprove = async () => {
    if (!selectedRequest || isPlanned === null) {
      return;
    }

    try {
      await leaveApiService.approveLeaveRequest(selectedRequest.id, isPlanned);
      setSnackbar({
        open: true,
        message: t("success.leaveRequestApprovedSuccessfully"),
        severity: "success",
      });
      setApproveDialogOpen(false);
      setSelectedRequest(null);
      setIsPlanned(null);
      fetchPendingRequests(); // Refresh the list
      triggerRefresh(); // Trigger badge refresh
    } catch (err) {
      setSnackbar({
        open: true,
        message:
          err instanceof Error
            ? err.message
            : t("error.failedToApproveRequest"),
        severity: "error",
      });
      setError(
        err instanceof Error ? err.message : t("error.failedToApproveRequest"),
      );
    }
  };

  const openDenyDialog = (request: LeaveRequest) => {
    setSelectedRequest(request);
    setIsPlanned(null);
    setDenialReason("");
    setDenyDialogOpen(true);
  };

  const handleDeny = async () => {
    if (!selectedRequest || !denialReason.trim() || isPlanned === null) {
      return;
    }

    try {
      await leaveApiService.denyLeaveRequest(
        selectedRequest.id,
        denialReason.trim(),
        isPlanned,
      );
      setSnackbar({
        open: true,
        message: t("leave.messages.requestDenied"),
        severity: "success",
      });
      setDenyDialogOpen(false);
      setSelectedRequest(null);
      setDenialReason("");
      setIsPlanned(null);
      fetchPendingRequests(); // Refresh the list
      triggerRefresh(); // Trigger badge refresh
    } catch (err) {
      setSnackbar({
        open: true,
        message:
          err instanceof Error
            ? err.message
            : t("leave.messages.failedToDenyRequest"),
        severity: "error",
      });
      setError(
        err instanceof Error
          ? err.message
          : t("leave.messages.failedToDenyRequest"),
      );
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <IconButton
            color="inherit"
            size="small"
            onClick={fetchPendingRequests}
          >
            <RefreshIcon />
          </IconButton>
        }
      >
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ pt: 0, pr: 2, pb: 2, pl: 2 }}>
      {requests.length === 0 ? (
        <Alert severity="info">{t("leave.noPendingRequests")}</Alert>
      ) : (
        <Grid container spacing={2}>
          {requests.map((request) => (
            <Grid size={{ xs: 12, md: 6 }} key={request.id}>
              <Card>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6">{request.employeeName}</Typography>
                    <Chip
                      label={t(`leave.leaveTypes.${request.leaveTypeCode}`)}
                      color="primary"
                      size="small"
                    />
                  </Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {formatDate(request.startDate)} -{" "}
                    {formatDate(request.endDate)}
                  </Typography>

                  <Typography variant="body2" gutterBottom>
                    <strong>
                      {request.totalDays} {t("leave.days")}
                    </strong>
                  </Typography>

                  {request.reason && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      {t("common.reason")}: {request.reason}
                    </Typography>
                  )}

                  <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      startIcon={<CheckIcon />}
                      onClick={() => openApproveDialog(request)}
                    >
                      {t("common.approve")}
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<CloseIcon />}
                      onClick={() => openDenyDialog(request)}
                    >
                      {t("common.deny")}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Approve Dialog with Planned/Unplanned Selection */}
      <Dialog
        open={approveDialogOpen}
        onClose={() => {
          setApproveDialogOpen(false);
          setSelectedRequest(null);
          setIsPlanned(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("leave.approveRequest")}</DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t("leave.requestDetails")}:
              </Typography>
              <Typography variant="body1">
                <strong>{selectedRequest.employeeName}</strong> -{" "}
                {selectedRequest.leaveTypeName}
              </Typography>
              <Typography variant="body2">
                {formatDate(selectedRequest.startDate)} -{" "}
                {formatDate(selectedRequest.endDate)} (
                {selectedRequest.totalDays} {t("leave.days")})
              </Typography>
            </Box>
          )}
          <FormControl component="fieldset" required sx={{ mt: 2 }}>
            <FormLabel component="legend">
              {t("leave.selectPlannedStatus")}
            </FormLabel>
            <RadioGroup
              row
              value={
                isPlanned === null ? "" : isPlanned ? "planned" : "unplanned"
              }
              onChange={(e) => setIsPlanned(e.target.value === "planned")}
            >
              <FormControlLabel
                value="planned"
                control={<Radio />}
                label={t("leave.planned")}
              />
              <FormControlLabel
                value="unplanned"
                control={<Radio />}
                label={t("leave.unplanned")}
              />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setApproveDialogOpen(false);
              setSelectedRequest(null);
              setIsPlanned(null);
            }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleApprove}
            color="success"
            variant="contained"
            disabled={isPlanned === null}
          >
            {t("common.approve")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Denial Reason Dialog with Planned/Unplanned Selection */}
      <Dialog
        open={denyDialogOpen}
        onClose={() => {
          setDenyDialogOpen(false);
          setSelectedRequest(null);
          setDenialReason("");
          setIsPlanned(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("leave.denyRequest")}</DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t("leave.requestDetails")}:
              </Typography>
              <Typography variant="body1">
                <strong>{selectedRequest.employeeName}</strong> -{" "}
                {selectedRequest.leaveTypeName}
              </Typography>
              <Typography variant="body2">
                {formatDate(selectedRequest.startDate)} -{" "}
                {formatDate(selectedRequest.endDate)} (
                {selectedRequest.totalDays} {t("leave.days")})
              </Typography>
            </Box>
          )}
          <FormControl component="fieldset" required sx={{ mt: 2, mb: 2 }}>
            <FormLabel component="legend">
              {t("leave.selectPlannedStatus")}
            </FormLabel>
            <RadioGroup
              row
              value={
                isPlanned === null ? "" : isPlanned ? "planned" : "unplanned"
              }
              onChange={(e) => setIsPlanned(e.target.value === "planned")}
            >
              <FormControlLabel
                value="planned"
                control={<Radio />}
                label={t("leave.planned")}
              />
              <FormControlLabel
                value="unplanned"
                control={<Radio />}
                label={t("leave.unplanned")}
              />
            </RadioGroup>
          </FormControl>
          <TextField
            autoFocus
            margin="dense"
            label={t("leave.denialReason")}
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={denialReason}
            onChange={(e) => setDenialReason(e.target.value)}
            placeholder={t("leave.denialReasonPlaceholder")}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDenyDialogOpen(false);
              setSelectedRequest(null);
              setDenialReason("");
              setIsPlanned(null);
            }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleDeny}
            color="error"
            variant="contained"
            disabled={!denialReason.trim() || isPlanned === null}
          >
            {t("common.deny")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <MuiAlert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default PendingApprovals;
